import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { Checkbox, IconButton, Typography } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { useEffect } from 'react'

import { persianRegex } from '../../helpers/regex'
import { StructuredData } from '../../types/rateConnection'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import useStyles from './styles/ExpandableBoxRow.styles'

interface ExpandableBoxRowProps {
  data: StructuredData
  depth: number
  expanded?: boolean
  setExpanded?: React.Dispatch<React.SetStateAction<boolean>>
  getRoomTypes?: LazyQueryExecFunction<
    {
      roomTypes: RoomTypeBrief[]
    },
    OperationVariables
  >
  getRatePlans?: LazyQueryExecFunction<
    {
      ratePlans: RatePlanBrief[]
    },
    OperationVariables
  >
  setSelectedAccommodationID?: React.Dispatch<React.SetStateAction<string>>
  structuredData: StructuredData[]
  setStructuredData: React.Dispatch<React.SetStateAction<StructuredData[]>>
  hideArrowForChannel?: boolean
  providerCode?: string
  disabled?: boolean
}

export default function ExpandableBoxRow({
  data,
  depth,
  expanded,
  setExpanded,
  getRoomTypes,
  getRatePlans,
  setSelectedAccommodationID,
  structuredData,
  setStructuredData,
  hideArrowForChannel,
  providerCode,
  disabled
}: ExpandableBoxRowProps) {
  const classes = useStyles({ depth })

  const handleExpand = () => {
    setExpanded?.(!expanded)
  }

  useEffect(() => {
    if (data.checked || expanded) {
      if (depth === 1 && data.children.length === 0) {
        getRoomTypes?.({
          variables: {
            propertyCode: data.code,
            showDeletedRoomTypes: false,
            showDeactivedRoomTypes: false,
            providerCode: providerCode
          }
        })
        getRatePlans?.({
          variables: {
            propertyCode: data.code,
            showDeletedRatePlans: false,
            showDeactivedRatePlans: false,
            providerCode: providerCode
          }
        })

        setSelectedAccommodationID?.(data.displayCode)
      }
    }
  }, [data.checked, expanded, data.code, providerCode])

  const updateChildrenCheckedState = (
    children: StructuredData[],
    newChecked: boolean,
    newIndeterminate: boolean
  ): StructuredData[] => {
    return children.map((child) => {
      const updatedChild = {
        ...child,
        checked: newChecked,
        indeterminate: newIndeterminate,
        children: updateChildrenCheckedState(
          child.children,
          newChecked,
          newIndeterminate
        )
      }
      return updatedChild
    })
  }

  const updateStructuredData = (
    nodes: StructuredData[],
    nodeId: string,
    newChecked: boolean
  ) => {
    const updatedNodes = nodes.map((node) => {
      if (node.id === nodeId) {
        const newIndeterminate = false
        node.checked = newChecked
        node.indeterminate = newIndeterminate
        node.children = updateChildrenCheckedState(
          node.children,
          newChecked,
          newIndeterminate
        )
      } else if (node.children && node.children.length > 0) {
        const result = updateStructuredData(node.children, nodeId, newChecked)
        node.children = result.nodes
        node.checked = node.children.every((child) => child.checked)
        if (node.children.some((child) => child.checked) && !node.checked) {
          node.indeterminate = true
        } else {
          node.indeterminate = false
        }
        if (node.children.some((child) => child.indeterminate)) {
          node.indeterminate = true
        }
      }

      return node
    })

    return { nodes: updatedNodes }
  }

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked

    const { nodes } = updateStructuredData(structuredData, data.id, isChecked)
    setStructuredData(nodes)
  }

  const isPersian = persianRegex.test((data && data.name) || '')

  return (
    <div
      className={[
        classes.channelRow,
        disabled ? classes.channelRowDisabled : ''
      ].join(' ')}>
      <Checkbox
        color="primary"
        checked={data.checked}
        indeterminate={data.indeterminate}
        onChange={handleCheck}
        disabled={disabled}
      />
      <Typography
        variant="h4"
        className={classes.channelName}
        style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
        {depth > 1 ? `${data.name} (${data.displayCode})` : data.name}
      </Typography>
      <IconButton className={classes.arrowIconContainer} onClick={handleExpand}>
        {!hideArrowForChannel && depth !== 3 ? (
          <>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</>
        ) : null}
      </IconButton>
    </div>
  )
}
